import React from "react";
import { DocumentUpload, Import, Edit, Edit2, CloseCircle } from "iconsax-react";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import { PERMISSIONS } from "@/shared/consts";
import type { LeaveApplication, LeaveStatus, LeaveType } from "../types";

export type LeaveAction =
  | "approve"
  | "reject"
  | "request_revision"
  | "revoke"
  | "sign_order"
  | "download_order_docx"
  | "upload_certificate";

export interface ActionConfig {
  id: LeaveAction;
  label: string;
  variant: "primary" | "secondary" | "danger";
  icon: React.ReactNode;
}

export interface LeaveActionsConfig {
  actions: ActionConfig[];
  isLoading: boolean;
}

interface IsLoadingState {
  isDownloadingOrder: boolean;
  isUpdatingCertificate: boolean;
  isRevisioning: boolean;
}

export function getAvailableActions(
  leave: LeaveApplication,
  isLoading: IsLoadingState,
  t: (key: string) => string,
): LeaveActionsConfig {
  const status = leave.status as LeaveStatus;
  const leaveType = leave.leave_type as LeaveType;
  const actions: ActionConfig[] = [];

  const hasRevisionNote = leave.application_review_note != null && leave.application_review_note !== "";
  const canApproveDraft = status === "draft" && !hasRevisionNote;
  const canRejectDraft = status === "draft" && !hasRevisionNote;
  const canRequestRevision = status === "draft" && !hasRevisionNote;
  const canRevoke = status === "signing_pending";
  const canSignOrder = status === "order_pending";
  const canDownloadOrderDocx =
    (status === "order_pending" || status === "order_uploaded" || status === "order_acknowledged" || status === "active") &&
    !leave.order?.signed_pdf_key;

  const hasCertificate =
    leaveType === "medical" && "certificate_pdf_url" in leave && leave.certificate_pdf_url !== null;
  const canUploadCertificate =
    leaveType === "medical" && (status === "order_uploaded" || status === "active") && !hasCertificate;

  if (canApproveDraft) {
    actions.push({
      id: "approve",
      label: t("actions.approve"),
      variant: "primary",
      icon: React.createElement(CheckIcon),
    });
  }

  if (canRequestRevision) {
    actions.push({
      id: "request_revision",
      label: t("actions.requestRevision"),
      variant: "secondary",
      icon: React.createElement(Edit2, { size: 16, color: "currentColor" }),
    });
  }

  if (canRejectDraft) {
    actions.push({
      id: "reject",
      label: t("actions.reject"),
      variant: "danger",
      icon: React.createElement(CloseIcon),
    });
  }

  if (canRevoke) {
    actions.push({
      id: "revoke",
      label: t("actions.revoke"),
      variant: "danger",
      icon: React.createElement(CloseCircle, { size: 16, color: "currentColor" }),
    });
  }

  if (canSignOrder) {
    actions.push({
      id: "sign_order",
      label: t("actions.signOrder"),
      variant: "primary",
      icon: React.createElement(Edit, { size: 16, color: "currentColor" }),
    });
  }

  if (canDownloadOrderDocx) {
    actions.push({
      id: "download_order_docx",
      label: isLoading.isDownloadingOrder ? t("actions.downloading") : t("actions.downloadOrderDocx"),
      variant: "secondary",
      icon: React.createElement(Import, { size: 16, color: "currentColor" }),
    });
  }

  if (canUploadCertificate) {
    actions.push({
      id: "upload_certificate",
      label: t("actions.uploadCertificate"),
      variant: "secondary",
      icon: React.createElement(DocumentUpload, { size: 16, color: "currentColor" }),
    });
  }

  const isLoadingAny =
    isLoading.isDownloadingOrder ||
    isLoading.isUpdatingCertificate ||
    isLoading.isRevisioning;

  return {
    actions,
    isLoading: isLoadingAny,
  };
}

export function canEditLeave(leave: LeaveApplication): boolean {
  const status = leave.status as LeaveStatus;
  return status === "draft";
}

export function filterActionsByPermissions(
  actions: ActionConfig[],
  hasPermission: (p: string) => boolean,
  hasRole: (r: string) => boolean,
): ActionConfig[] {
  return actions.filter(function filterByPermission(action) {
    if (action.id === "approve" || action.id === "sign_order") {
      return hasPermission(PERMISSIONS.HR.APPROVE_LEAVE) && hasRole("chief");
    }
    if (action.id === "reject" || action.id === "request_revision" || action.id === "revoke") {
      return hasPermission(PERMISSIONS.HR.APPROVE_LEAVE);
    }
    return true;
  });
}
